import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  School as SchoolIcon,
  Feedback as FeedbackIcon,
  Pending as PendingIcon,
  Group as GroupIcon,
  Security as SecurityIcon,
  Policy as PolicyIcon,
} from '@mui/icons-material';

// Tab Components
import DocumentsTab from '../components/existing/DocumentsTab';
import TrainingTab from '../components/existing/TrainingTab';
import FeedbackTab from '../components/existing/FeedbackTab';
import KpikriTab from '../components/existing/KpikriTab';
import KpiProposalTab from '../components/existing/KpiProposalTab';
import BrandSurveyTab from '../components/existing/BrandSurveyTab';
import GrievanceFormTab from '../components/existing/GrievanceFormTab';
import PoshComplaintTab from '../components/existing/PoshComplaintTab';
import MyTeamTab from '../components/existing/MyTeamTab';
import AccessProvisioningTab from '../components/existing/AccessProvisioningTab';
import ResourceTab from '../components/existing/ResourceTab';
import UpdatedPoliciesTab from '../components/existing/UpdatedPoliciesTab';
import { useAuth } from '../context/AuthContext';

const ExistingEmployee = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOption, setSelectedOption] = useState('documents');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { user } = useAuth();

  const options = [
    { key: 'documents', label: 'Documents', icon: <DescriptionIcon />, component: DocumentsTab },
    { key: 'updated_policies', label: 'Updated Policies', icon: <PolicyIcon />, component: UpdatedPoliciesTab },
    { key: 'training', label: 'Training', icon: <SchoolIcon />, component: TrainingTab },
    { key: 'feedback', label: 'Feedback', icon: <FeedbackIcon />, component: FeedbackTab },
    { key: 'my_team', label: 'My Team', icon: <GroupIcon />, component: MyTeamTab },
    { key: 'kpi_kri', label: 'KPI, KRA', icon: <PendingIcon />, component: KpikriTab },
    { key: 'kpi_proposal', label: 'Propose KPIs', icon: <PendingIcon />, component: KpiProposalTab },
    { key: 'brand_survey', label: 'Brand Survey', icon: <PendingIcon />, component: BrandSurveyTab , disabled: true },
    { key: 'grievance_form', label: 'Grievance Form', icon: <PendingIcon />, component: GrievanceFormTab },
    { key: 'posh_complaint', label: 'POSH Complaint', icon: <PendingIcon />, component: PoshComplaintTab },
    { key: 'access_provisioning', label: 'Access Provisioning', icon: <SecurityIcon />, component: AccessProvisioningTab },
    { key: 'resource', label: 'Resources', icon: <PendingIcon />, component: ResourceTab },
  ];

  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        setLoading(true);
        await new Promise((resolve) => setTimeout(resolve, 1000));
      } catch (err) {
        setError('Failed to load employee data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchEmployeeData();
  }, []);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam && options.some(option => option.key === tabParam && !option.disabled)) {
      setSelectedOption(tabParam);
    }
  }, [searchParams]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  // If no user is found yet
  if (!user) return <div>Loading...</div>;
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, p: { xs: 2, sm: 3 } }}>
      <Container maxWidth={false} disableGutters sx={{ width: '100%' }}>
      <Box sx={{ mb: 4, mt: 2 }}>
        <Typography  gutterBottom sx={{ fontWeight: 600, color: 'text.primary', letterSpacing: 1 ,fontSize: 30 }}>
          Welcome, {user.firstName}  {user.lastName}, to Divergent Insights!
        </Typography>
        <Typography  color="text.secondary" gutterBottom sx={{ fontStyle: 'italic', fontWeight: 400, mb: 3 ,fontSize: 20}}>
        Creating Value Through Collaboration 
        </Typography>
      </Box>
        <Paper sx={{ p: { xs: 2, sm: 4 }, width: '100%' }}>
          {/* <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Employee Portal
          </Typography> */}
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4, fontSize: '0.9rem' }}>
          This section allows easy access to managing your documents, trainings, share feedback, raise grievances with ease and POSH-compliant support to ensure a safe and supportive workplace.
          </Typography>

          {/* Option Cards */}
          <Grid
            container
            spacing={2}
            justifyContent={isMobile ? 'center' : 'flex-start'}
            sx={{ mb: 4 }}
          >
            {options.map((option) => (
              <Grid item xs={6} sm="auto" key={option.key}>
                <Card
                  sx={{
                    minWidth: 160,
                    minHeight: 130,
                    boxShadow: selectedOption === option.key ? 6 : 1,
                    backgroundColor: option.disabled 
                      ? 'grey.100' 
                      : selectedOption === option.key 
                        ? 'primary.100' 
                        : 'background.paper',
                    color: option.disabled 
                      ? 'grey.500' 
                      : selectedOption === option.key 
                        ? 'primary.main' 
                        : 'text.primary',
                    border: selectedOption === option.key ? '2px solid #1976d2' : '1px solid #e0e0e0',
                    transition: 'all 0.2s',
                    cursor: option.disabled ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    px: 1,
                    opacity: option.disabled ? 0.6 : 1,
                  }}
                  onClick={() => !option.disabled && setSelectedOption(option.key)}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
                    {option.icon}
                    <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 500,fontSize: '0.9rem' }}>
                      {option.label}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Selected Tab Content */}
          <Box sx={{ mt: 2 }}>
            {options.find((o) => o.key === selectedOption)?.component &&
              React.createElement(options.find((o) => o.key === selectedOption).component)}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ExistingEmployee;
